import { isValidRefreshToken } from './authTokens';
// Only used when running inside the Tauri desktop app (window.__TAURI__ present)

export interface Renderer {
  id: string;
  name: string;
  location: string;
  control_url: string;
  volume_url: string;
  ip: string;
  model: string;
  manufacturer: string;
  transport_type?: string;
  raat_port?: number | null;
  is_roon_ready?: boolean;
  uses_stop_for_pause?: boolean;
  sink_protocols?: string[];
  can_play?: boolean;
  icon_url?: string;
  custom_name?: string | null;
}

export function isConnectorAvailable(): boolean {
  return typeof window !== 'undefined' && '__TAURI__' in window;
}

async function invoke<T>(command: string, args?: Record<string, unknown>): Promise<T> {
  if (!isConnectorAvailable()) throw new Error('Maestro not available');
  // WebKit blocks ipc:// as "insecure content" on HTTPS pages until Tauri
  // falls back to postMessage. Retry so early calls survive the switchover.
  for (let attempt = 0; attempt < 4; attempt++) {
    try {
      return await (window as any).__TAURI__.core.invoke(command, args);
    } catch (err) {
      const isIpcBlock = String(err).includes('Load failed') || String(err).includes('not allowed');
      if (isIpcBlock && attempt < 3) {
        await new Promise(r => setTimeout(r, 300 * (attempt + 1)));
      } else {
        throw err;
      }
    }
  }
  throw new Error(`invoke('${command}') failed after retries`);
}

export async function listen(event: string, callback: (payload: any) => void): Promise<() => void> {
  if (!isConnectorAvailable()) return () => {};
  // WebKit blocks ipc:// as "insecure content" from HTTPS pages. Tauri falls
  // back to postMessage, but in-flight listen() calls fail before the fallback
  // activates. Retry so the subscription is established after the switchover.
  for (let attempt = 0; attempt < 6; attempt++) {
    try {
      const unlisten = await (window as any).__TAURI__.event.listen(event, (e: any) => callback(e.payload));
      if (attempt > 0) console.log(`[connector] listen('${event}') succeeded on attempt ${attempt + 1}`);
      return unlisten;
    } catch (err) {
      if (attempt < 5) {
        await new Promise(r => setTimeout(r, 400 * (attempt + 1)));
      } else {
        console.error(`[connector] listen('${event}') failed after 6 attempts:`, err);
        throw err;
      }
    }
  }
  return () => {};
}

// Discovery
export async function getRenderers(): Promise<Renderer[]> {
  return invoke<Renderer[]>('get_renderers');
}

export async function probeDeviceIp(ip: string): Promise<string> {
  console.log('[connector.probeDeviceIp] probing:', ip);
  return invoke<string>('probe_device_ip', { ip });
}

export async function onRenderersUpdated(cb: (renderers: Renderer[]) => void): Promise<() => void> {
  return listen('renderers-updated', cb);
}

// Transport
export async function play(rendererId: string) {
  return invoke('play', { rendererId });
}

export async function pause(rendererId: string) {
  return invoke('pause', { rendererId });
}

export async function stop(rendererId: string) {
  return invoke('stop', { rendererId });
}

/** Idle sleep prevention while playing (Connector desktop). Failures are logged only. */
export async function holdAwake(): Promise<void> {
  if (!isConnectorAvailable()) return;
  try {
    await invoke('hold_awake');
  } catch (e) {
    console.warn('[PowerManager] hold_awake failed (non-fatal):', e);
  }
}

/** Release idle sleep prevention when not playing. Failures are logged only. */
export async function releaseAwake(): Promise<void> {
  if (!isConnectorAvailable()) return;
  try {
    await invoke('release_awake');
  } catch (e) {
    console.warn('[PowerManager] release_awake failed (non-fatal):', e);
  }
}

/** Network-activity sleep prevention (alias for holdAwake). Failures are logged only. */
export async function holdAwakeNetwork(): Promise<void> {
  return holdAwake();
}

export async function seek(rendererId: string, seconds: number) {
  return invoke('seek', { rendererId, seconds: Math.floor(seconds) });
}

export async function getVolume(rendererId: string): Promise<number> {
  return invoke('get_volume', { rendererId }) as Promise<number>;
}

export async function setVolume(rendererId: string, volume: number) {
  const vol = Math.round(Math.min(100, Math.max(0, volume)));
  console.log('[connector.setVolume] invoke set_volume:', rendererId, vol);
  return invoke('set_volume', { rendererId, volume: vol });
}

export async function setUri(rendererId: string, uri: string, metadata = '') {
  return invoke('set_uri', { rendererId, uri, metadata });
}

export async function setNextUri(
  rendererId: string,
  uri: string,
  title: string,
  artist: string,
  album: string,
  formatId: number,
  durationSecs: number,
  artUrl?: string,
) {
  return invoke('set_next_uri', {
    rendererId,
    uri,
    title,
    artist,
    album,
    formatId,
    durationSecs: durationSecs,
    artUrl: artUrl ?? null,
  });
}

export async function playUri(
  rendererId: string,
  uri: string,
  title: string,
  artist: string,
  album: string,
  formatId: number,
  durationSecs: number,
  artUrl?: string,
  startSeconds?: number,
) {
  const payload = {
    uri,
    format_id: formatId,
    renderer_id: rendererId,
    title,
    artist,
    album,
    art_url: artUrl ?? null,
    duration: durationSecs,
    start_seconds: startSeconds != null && startSeconds > 1 ? Math.floor(startSeconds) : null,
    // Keep camelCase aliases for Tauri argument compatibility.
    rendererId,
    formatId,
    durationSecs,
    artUrl: artUrl ?? null,
    startSeconds: startSeconds != null && startSeconds > 1 ? Math.floor(startSeconds) : null,
  };
  console.log('[Bridge] invoke payload:', payload);
  return invoke('play_uri', payload);
}

export async function getTransportInfo(rendererId: string): Promise<string> {
  return invoke<string>('get_transport_info', { rendererId });
}

export interface MultizonePlayResult {
  renderer_id: string;
  success: boolean;
  error: string | null;
}

export async function multizonePlay(
  renderers: { renderer_id: string; volume: number | null }[],
  uri: string,
  title: string,
  artist: string,
  album: string,
  formatId: number,
  durationSecs: number,
  artUrl?: string,
): Promise<MultizonePlayResult[]> {
  console.log('[Bridge] multizonePlay:', renderers.length, 'zones');
  return invoke<MultizonePlayResult[]>('multizone_play', {
    renderers,
    uri,
    title,
    artist,
    album,
    formatId,
    format_id: formatId,
    durationSecs,
    duration_secs: durationSecs,
    artUrl: artUrl ?? null,
    art_url: artUrl ?? null,
  });
}

export async function multizonePause(): Promise<void> {
  console.log('[Bridge] multizonePause — cancelling drift monitor');
  return invoke<void>('multizone_pause');
}

export async function multizoneStop(): Promise<void> {
  console.log('[Bridge] multizoneStop — cancelling drift monitor + stopping all');
  return invoke<void>('multizone_stop');
}

// Set a user-chosen custom name for a network player. Persists across restarts.
// Pass empty string to clear the custom name and revert to the device's own name.
export async function renameRenderer(rendererId: string, customName: string): Promise<void> {
  return invoke<void>('rename_renderer', { rendererId, customName });
}

// Volume state feedback from device (physical knob, network renderer events)
export async function onVolumeChanged(cb: (data: { rendererId: string; volume: number }) => void): Promise<() => void> {
  return listen('volume-changed', cb);
}

// Transport state feedback — emitted when the device's real playback state changes
// (e.g. PLAYING, STOPPED, PAUSED_PLAYBACK, NO_MEDIA_PRESENT, TRANSITIONING)
export async function onTransportStateChanged(
  cb: (data: { rendererId: string; state: string }) => void,
): Promise<() => void> {
  return listen('transport-state-changed', cb);
}

// ---------------------------------------------------------------------------
// Local Library — Scanner + Folder Browser + Local File Playback
// iOS PARITY: These IPC calls only work inside the Connector (Tauri).
// iOS reads library data from Supabase after the Connector scans.
// ---------------------------------------------------------------------------

export interface ScannedTrack {
  file_path: string;
  title: string | null;
  artist: string | null;
  album: string | null;
  album_artist: string | null;
  year: number | null;
  genre: string | null;
  track_num: number | null;
  disc_num: number | null;
  duration: number | null;
  sample_rate: number | null;
  bit_depth: number | null;
  channels: number | null;
  format: string;
  file_size: number;
  has_cover_art: boolean;
  /** SHA256 of embedded ID3v2 / FLAC PICTURE / MP4 covr / DSF APIC bytes
   *  after the scanner saves them into the local cover_cache. The hash
   *  is the URL path segment for `http://127.0.0.1:{proxy_port}/cover/{hash}`. */
  embedded_cover_hash?: string | null;
  /** SHA256 of folder.jpg / cover.jpg / front.jpg sidecar bytes after
   *  ingest into cover_cache. */
  folder_cover_hash?: string | null;
}

export interface ScanProgress {
  files_found: number;
  files_processed: number;
  albums_identified: number;
  current_file: string;
  percent: number;
  eta_seconds: number;
  phase: string;
}

export interface ScanSummary {
  total_tracks: number;
  total_albums: number;
  duration_secs: number;
  files_unchanged?: number;
  files_new_or_changed?: number;
  incremental_enabled?: boolean;
  skipped_sources?: string[];
  walk_errors?: number;
}

export async function listDirectory(path: string): Promise<{ name: string; path: string; is_dir: boolean }[]> {
  try {
    return await invoke<{ name: string; path: string; is_dir: boolean }[]>('list_directory', { path });
  } catch {
    return [];
  }
}

export async function checkSourcesAccessible(paths: string[]): Promise<Record<string, boolean>> {
  const result: Record<string, boolean> = {};
  for (const p of paths) {
    try {
      const entries = await listDirectory(p);
      result[p] = entries.length > 0;
    } catch {
      result[p] = false;
    }
  }
  return result;
}

// iOS PARITY: iOS cannot pick local folders — local library scanning
// is desktop-only. iOS accesses local files via BATON remote streaming.
// This function must never be called on iOS.
export async function pickMusicFolders(): Promise<string[]> {
  if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) return [];
  const result = await invoke<string[]>('pick_music_folders');
  return result || [];
}

export async function scanLibrary(
  paths: string[],
  sources?: Array<{ id: string; path: string }>,
): Promise<ScanSummary> {
  if (sources && sources.length > 0) {
    return invoke<ScanSummary>('scan_library', { sources });
  }
  return invoke<ScanSummary>('scan_library', { paths });
}

/** Local-core scan — writes to on-device library DB; emits local-core:scan-progress. */
export async function scanLocalLibrarySources(
  sources: Array<{ id: string; path: string; scan_path?: string }>,
): Promise<ScanSummary> {
  const resp = await invoke<{
    success?: boolean;
    total_tracks?: number;
    total_albums?: number;
    files_new_or_changed?: number;
    files_unchanged?: number;
    skipped_sources?: string[];
    walk_errors?: number;
    error?: string;
  }>('local_library_scan_sources', { sources });
  if (resp?.success === false || resp?.error) {
    throw new Error(resp?.error || 'Local library scan failed');
  }
  return {
    total_tracks: resp?.total_tracks ?? 0,
    total_albums: resp?.total_albums ?? 0,
    duration_secs: 0,
    files_new_or_changed: resp?.files_new_or_changed,
    files_unchanged: resp?.files_unchanged,
    incremental_enabled: true,
    skipped_sources: resp?.skipped_sources,
    walk_errors: resp?.walk_errors,
  };
}

export async function onLocalScanProgress(
  cb: (event: Record<string, unknown>) => void,
): Promise<() => void> {
  return listen('local-core:scan-progress', (evt) => {
    const payload = (evt as { payload?: Record<string, unknown> })?.payload ?? evt;
    cb(payload as Record<string, unknown>);
  });
}

export async function cancelScan(): Promise<void> {
  return invoke<void>('cancel_scan');
}

export async function onScanBatch(
  cb: (tracks: ScannedTrack[]) => void,
): Promise<() => void> {
  return listen('scan-batch', cb);
}

export async function playLocalFile(
  filePath: string,
  rendererId: string,
  title: string,
  artist: string,
  album: string,
  durationSecs: number,
  artworkId?: string | null,
  sampleRate?: number | null,
  bitDepth?: number | null,
  channelCount?: number | null,
  fileSize?: number | null,
): Promise<string> {
  return invoke<string>('play_local_file', {
    filePath,
    rendererId,
    title,
    artist,
    album,
    durationSecs,
    artworkId: artworkId ?? null,
    sampleRate: sampleRate ?? null,
    bitDepth: bitDepth ?? null,
    channelCount: channelCount ?? null,
    fileSize: fileSize ?? null,
  });
}

export async function onScanProgress(
  cb: (progress: ScanProgress) => void,
): Promise<() => void> {
  return listen('scan-progress', cb);
}

// ---------------------------------------------------------------------------
// Remote Access — Register a local file and return a proxy URL without playing
// Used when this Connector is serving files to a remote Maestro client.
// iOS PARITY: iOS would expose an equivalent via the native bridge.
// ---------------------------------------------------------------------------

export async function registerFileUrl(filePath: string): Promise<string> {
  return invoke<string>('register_file_url', { filePath });
}

/**
 * Set the allowed library source directories on the Connector.
 * Only files under these paths can be served via the proxy.
 */
export async function setLibraryPaths(paths: string[]): Promise<void> {
  return invoke<void>('set_library_paths', { paths });
}

// ---------------------------------------------------------------------------
// Cloudflare Tunnel — query the current tunnel URL from the Connector
// iOS PARITY: iOS fetches tunnel URL from Mycelium, not from Tauri IPC.
// ---------------------------------------------------------------------------

export async function getTunnelUrl(): Promise<string | null> {
  return invoke<string | null>('get_tunnel_url');
}

/** Live tunnel URL from the background daemon (Unix socket IPC). */
export async function queryDaemonTunnelUrl(): Promise<string | null> {
  try {
    const url = await invoke<string | null>('query_daemon_tunnel_url');
    return url || null;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Headless Service — install/uninstall auto-start on login
// ---------------------------------------------------------------------------

export async function startHeadlessService(): Promise<void> {
  return invoke<void>('start_headless_service');
}

export async function stopHeadlessService(): Promise<void> {
  return invoke<void>('stop_headless_service');
}

/** Return this machine's stable identifier (hostname-uuid). */
export async function getMachineId(): Promise<string> {
  return invoke<string>('get_machine_id');
}

/** Auto-enable remote access: install background service + ensure tunnel is running. */
export async function autoEnableRemoteAccess(): Promise<void> {
  return invoke<void>('auto_enable_remote_access');
}

/** Trigger macOS Local Network permission on the home library host. */
export async function warmLocalNetworkAccess(): Promise<void> {
  if (!isConnectorAvailable()) return;
  try {
    await invoke<void>('warm_local_network_access');
  } catch (e) {
    console.warn('[warmLocalNetworkAccess]', e);
  }
}

/** Poll for a live tunnel URL (daemon IPC or in-app cloudflared) and register with Mycelium. */
export async function registerLiveTunnelWithCloud(
  maxWaitMs = 45000,
): Promise<string | null> {
  if (!isConnectorAvailable()) return null;
  const deadline = Date.now() + maxWaitMs;
  while (Date.now() < deadline) {
    const url = (await queryDaemonTunnelUrl()) || (await getTunnelUrl());
    if (url) {
      try {
        const machineId = await getMachineId();
        const { libraryService } = await import('../services/libraryService');
        await libraryService.registerTunnel(url, machineId);
        try {
          const { useMaestroStore } = await import('../store/maestroStore');
          useMaestroStore.getState().setTunnelConnected(true);
        } catch { /* optional */ }
        return url;
      } catch (e) {
        console.warn('[registerLiveTunnelWithCloud] register failed:', e);
        return url;
      }
    }
    await new Promise((r) => setTimeout(r, 2000));
  }
  return null;
}

/** Full remote-access setup: auth for background service + daemon install + tunnel. */
export async function enableHomeLibraryRemoteAccess(options?: {
  accessToken?: string | null;
  refreshToken?: string | null;
  userId?: string | null;
  email?: string | null;
}): Promise<void> {
  const { resolveRefreshTokenForDaemon } = await import('../components/AuthView');
  const resolved = await resolveRefreshTokenForDaemon();

  const refreshToken = (options?.refreshToken && isValidRefreshToken(options.refreshToken))
    ? options.refreshToken
    : resolved.refreshToken;
  const userId = options?.userId ?? resolved.userId ?? '';
  const email = options?.email ?? resolved.email ?? '';
  const accessToken = options?.accessToken ?? resolved.accessToken ?? null;

  if (isValidRefreshToken(refreshToken)) {
    await writeAuthJson(refreshToken, userId, email);
  } else {
    console.warn('[enableHomeLibraryRemoteAccess] No valid refresh token — installing service anyway; sign out and back in if tunnel fails to register');
  }
  if (accessToken) {
    await setAuthToken(accessToken);
  }
  await warmLocalNetworkAccess();
  await autoEnableRemoteAccess();

  // Verify the LaunchAgent actually installed — auto_enable can swallow Rust errors.
  if (isConnectorAvailable()) {
    await new Promise((r) => setTimeout(r, 2500));
    try {
      let verification = await verifyDaemonInstallation();
      let basic = await getDaemonStatus().catch(() => null);
      let installed =
        verification.plist_exists ||
        (basic?.installed === true);

      if (!installed || (!verification.daemon_running && basic && !basic.running)) {
        // Retry install + start once (fixes corrupt 0-byte binary from older builds).
        console.warn('[enableHomeLibraryRemoteAccess] daemon not healthy — reinstalling');
        try {
          await installDaemon();
        } catch (installErr) {
          console.warn('[enableHomeLibraryRemoteAccess] installDaemon failed:', installErr);
        }
        await startDaemon().catch(() => {});
        await autoEnableRemoteAccess().catch(() => {});
        await new Promise((r) => setTimeout(r, 3000));
        verification = await verifyDaemonInstallation();
        basic = await getDaemonStatus().catch(() => null);
        installed =
          verification.plist_exists ||
          (basic?.installed === true);
      }

      if (!installed) {
        throw new Error('Background service did not install. Try “Install Background Service” below, or restart Maestro.');
      }
      const daemonHealthy = verification.daemon_running || (basic?.running === true);
      if (!daemonHealthy) {
        const liveUrl = await registerLiveTunnelWithCloud(45000);
        if (liveUrl) {
          console.warn(
            '[enableHomeLibraryRemoteAccess] Background service not running — registered in-app tunnel (works while Maestro stays open). Check ~/Library/Logs/MaestroConnector.log if the service keeps failing.',
          );
          return;
        }
        throw new Error(
          'Background service installed but not running, and no tunnel could be started. Try “Reinstall Service” below, or check ~/Library/Logs/MaestroConnector.log',
        );
      }
    } catch (e) {
      if (e instanceof Error && (
        e.message.includes('Background service') ||
        e.message.includes('not running')
      )) throw e;
      // verifyDaemonInstallation unavailable on older builds — continue
    }
  }

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('maestro:remote-access-changed'));
  }
}

/** Shut down remote access: stop tunnel, uninstall login item. */
export async function shutdownRemoteAccess(): Promise<void> {
  return invoke<void>('shutdown_remote_access');
}

/** Check whether the headless service plist exists and is loaded. */
export async function checkHeadlessServiceStatus(): Promise<{
  plist_exists: boolean;
  service_loaded: boolean;
}> {
  return invoke<{ plist_exists: boolean; service_loaded: boolean }>(
    'check_headless_service_status',
  );
}

/** Persist the user's JWT so headless mode can register tunnels with Mycelium. */
export async function setAuthToken(token: string): Promise<void> {
  return invoke<void>('set_auth_token', { token });
}

/** Listen for tunnel URL changes (emitted by Rust when cloudflared establishes a new tunnel). */
export async function onTunnelUrlChanged(
  cb: (url: string) => void,
): Promise<() => void> {
  return listen('tunnel-url-changed', cb);
}

// ---------------------------------------------------------------------------
// Daemon management — install, uninstall, start, stop, verify
// ---------------------------------------------------------------------------

export interface DaemonVerification {
  plist_exists: boolean;
  daemon_running: boolean;
  tunnel_url_registered: boolean;
  update_available: boolean;
  update_version: string | null;
  error: string | null;
}

/** Get basic daemon status (installed, running, pid). Works on all Connector versions. */
export async function getDaemonStatus(): Promise<{
  installed: boolean;
  running: boolean;
  pid: number | null;
  tunnel_url: string | null;
}> {
  return invoke('get_daemon_status');
}

/** Comprehensive daemon installation verification for Settings UI. */
export async function verifyDaemonInstallation(): Promise<DaemonVerification> {
  return invoke<DaemonVerification>('verify_daemon_installation');
}

/** Install the background service daemon (macOS LaunchAgent / Windows Service). */
export async function installDaemon(): Promise<void> {
  return invoke<void>('install_daemon');
}

/** Uninstall the background service daemon. */
export async function uninstallDaemon(): Promise<void> {
  return invoke<void>('uninstall_daemon');
}

/** Full uninstall — daemon + all Maestro data from disk. */
export async function fullUninstall(): Promise<string> {
  return invoke<string>('full_uninstall');
}

/** Start the daemon via launchctl/sc. */
export async function startDaemon(): Promise<void> {
  return invoke<void>('start_daemon');
}

/** Stop the daemon via launchctl/sc. */
export async function stopDaemon(): Promise<void> {
  return invoke<void>('stop_daemon');
}

/** Read persisted auth credentials from auth.json (session restore on cold start). */
export async function readAuthJson(): Promise<{
  refresh_token: string;
  user_id: string;
  email: string;
} | null> {
  try {
    return await invoke<{ refresh_token: string; user_id: string; email: string }>('read_auth_json');
  } catch {
    return null;
  }
}

/** Write auth credentials for the daemon. Validates token length. */
export async function writeAuthJson(
  refreshToken: string,
  userId: string,
  email: string,
): Promise<void> {
  const len = (refreshToken || '').length;
  console.log(`[writeAuthJson] refresh_token length=${len}, userId=${userId}`);
  if (!isValidRefreshToken(refreshToken)) {
    const msg = `[writeAuthJson] REJECTED — invalid refresh_token (len=${len})`;
    console.error(msg);
    throw new Error(msg);
  }
  return invoke<void>('write_auth_json', {
    refreshToken,
    userId,
    email,
  });
}

/** Get Connector version, platform, and build timestamp. */
export async function getConnectorInfo(): Promise<{ version: string; platform: string; build_timestamp?: string }> {
  return invoke<{ version: string; platform: string; build_timestamp?: string }>('get_connector_info');
}

// ---------------------------------------------------------------------------
// Local cover-art proxy port
// ---------------------------------------------------------------------------
//
// The Connector's HTTP proxy serves embedded/folder cover art from a
// content-addressable cache at `GET /cover/{hash}`. The proxy port is
// chosen at startup so the frontend has to ask for it once, cache it on
// the global, and then build URLs synchronously from any component.
//
// Browser/iOS preview (no Connector): all calls return null and the
// online cover URL takes over via the existing fallback chain.

const PROXY_PORT_GLOBAL = '__MAESTRO_PROXY_PORT';
let proxyPortBootstrap: Promise<number | null> | null = null;

/** Fetch the local proxy port via IPC. Returns null when not running
 *  inside the Connector or when the proxy hasn't bound yet. */
export async function getProxyPort(): Promise<number | null> {
  if (!isConnectorAvailable()) return null;
  try {
    const port = await invoke<number>('get_proxy_port');
    return port > 0 ? port : null;
  } catch (err) {
    console.warn('[connector] get_proxy_port failed:', err);
    return null;
  }
}

/** Idempotent bootstrap: invoke `get_proxy_port` once at app init and
 *  cache the result on `window.__MAESTRO_PROXY_PORT`. Subsequent calls
 *  return the cached value without hitting IPC. Safe to call from
 *  multiple components — concurrent callers share the same in-flight
 *  promise. */
export async function ensureProxyPort(): Promise<number | null> {
  if (typeof window === 'undefined') return null;
  const cached = (window as any)[PROXY_PORT_GLOBAL];
  if (typeof cached === 'number' && cached > 0) return cached;
  if (proxyPortBootstrap) return proxyPortBootstrap;
  proxyPortBootstrap = (async () => {
    const port = await getProxyPort();
    if (port && port > 0) {
      (window as any)[PROXY_PORT_GLOBAL] = port;
    }
    return port;
  })();
  return proxyPortBootstrap;
}

/** True when `http://127.0.0.1:{port}/cover/…` img/fetch URLs are blocked
 *  (HTTPS Pages shell loading inside Connector WebView). Use Tauri IPC instead. */
export function isHttpsCoverContext(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.location.protocol !== 'https:') return false;
  const host = window.location.hostname;
  return host !== 'localhost' && host !== '127.0.0.1';
}

/** Build a direct `http://127.0.0.1:{port}/cover/{hash}` URL. Works only on
 *  same-origin HTTP (legacy). On HTTPS sandbox/production, returns null — use
 *  coverArtIPC (`read_cover_art`) instead. */
export function buildLocalCoverUrl(hash?: string | null): string | null {
  if (!hash || typeof hash !== 'string') return null;
  if (typeof window === 'undefined') return null;
  if (isHttpsCoverContext()) return null;
  const port = (window as any)[PROXY_PORT_GLOBAL];
  if (typeof port !== 'number' || port <= 0) return null;
  return `http://127.0.0.1:${port}/cover/${hash}`;
}

const _coverBlobCache = new Map<string, string>();
const _coverFetchInFlight = new Map<string, Promise<string | null>>();

/** Fetch cover art bytes from the local proxy via fetch() and return a
 *  blob: URL. This bypasses mixed-content restrictions that can block
 *  `<img src="http://...">` on HTTPS pages in some contexts (e.g. the
 *  album grid). Results are cached — the same hash always returns the
 *  same blob URL. Returns null on error. */
export function fetchLocalCoverUrl(hash?: string | null): Promise<string | null> {
  if (!hash || typeof hash !== 'string') return Promise.resolve(null);
  if (typeof window === 'undefined') return Promise.resolve(null);
  if (isHttpsCoverContext()) return Promise.resolve(null);
  const port = (window as any)[PROXY_PORT_GLOBAL];
  if (typeof port !== 'number' || port <= 0) return Promise.resolve(null);

  const cached = _coverBlobCache.get(hash);
  if (cached) return Promise.resolve(cached);

  const inflight = _coverFetchInFlight.get(hash);
  if (inflight) return inflight;

  const promise = (async (): Promise<string | null> => {
    try {
      const resp = await fetch(`http://127.0.0.1:${port}/cover/${hash}`);
      if (!resp.ok) return null;
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      _coverBlobCache.set(hash, url);
      return url;
    } catch {
      return null;
    } finally {
      _coverFetchInFlight.delete(hash);
    }
  })();

  _coverFetchInFlight.set(hash, promise);
  return promise;
}

// ---------------------------------------------------------------------------
// macOS Audio Output Device Selection
// ---------------------------------------------------------------------------

export interface AudioOutputDevice {
  id: string;
  name: string;
  is_default: boolean;
  /** "usb", "built_in", "bluetooth", "hdmi", "airplay", "virtual", "aggregate", "thunderbolt", "displayport", "pci", "firewire", "unknown" */
  transport_type: string;
  manufacturer: string;
  /** CoreAudio device UID — stable across reboots, contains USB product info */
  uid: string;
  /** Heuristic: true for standalone USB DACs, false for USB monitors/speakers/headsets */
  is_likely_dac: boolean;
}

export async function getAudioOutputDevices(): Promise<AudioOutputDevice[]> {
  return invoke<AudioOutputDevice[]>('get_audio_output_devices');
}

export async function setAudioOutputDevice(deviceId: string): Promise<void> {
  return invoke<void>('set_audio_output_device', { deviceId });
}

/** Query real hardware-supported formats from a USB DAC's physical streams. */
export async function getDeviceHardwareFormats(deviceId: string): Promise<{
  formats: Array<{
    sample_rate: number;
    bit_depth: number;
    channels: number;
    /** "pcm" | "dsd" | "dsd_native" */
    format_type?: string;
    /** DSD rate label: "DSD64", "DSD128", "DSD256", "DSD512" */
    dsd_rate?: string | null;
  }>;
  max_sample_rate: number;
  max_bit_depth: number;
  source?: string;
}> {
  return invoke('get_device_hardware_formats', { deviceId });
}

/** Take or release CoreAudio hog mode on a USB DAC. Returns the new state (true = hogged). */
export async function setExclusiveMode(deviceId: string, enable: boolean): Promise<boolean> {
  return invoke<boolean>('set_exclusive_mode', { deviceId, enable });
}

/** Set device sample rate to match content, preventing CoreAudio resampling. Returns the rate actually set. */
export async function setDeviceSampleRate(deviceId: string, sampleRate: number): Promise<number> {
  return invoke<number>('set_device_sample_rate', { deviceId, sampleRate });
}

// ─── Exclusive (bit-perfect) audio engine ────────────────────────────────────

export interface ExclusivePlaybackState {
  is_playing: boolean;
  position_secs: number;
  duration_secs: number;
  sample_rate: number;
  bit_depth: number;
  channels: number;
  is_buffering: boolean;
}

/** Start bit-perfect playback: fetches, decodes, writes PCM directly to USB DAC via HAL AudioUnit. */
export async function exclusivePlay(deviceName: string, url: string, sampleRate: number, bitDepth: number): Promise<void> {
  return invoke<void>('exclusive_play', { deviceName, url, sampleRate, bitDepth });
}

export async function exclusivePause(): Promise<void> {
  return invoke<void>('exclusive_pause');
}

export async function exclusiveResume(): Promise<void> {
  return invoke<void>('exclusive_resume');
}

export async function exclusiveStop(): Promise<void> {
  return invoke<void>('exclusive_stop');
}

export async function exclusiveGetState(): Promise<ExclusivePlaybackState> {
  return invoke<ExclusivePlaybackState>('exclusive_get_state');
}

export interface UsbDriverStatus {
  has_driver: boolean;
  driver_name: string | null;
  needs_install: boolean;
  vendor_url: string | null;
  error?: string;
}

/** Check if a USB DAC has a working driver (Windows) or native support (macOS). */
export async function checkUsbDriverStatus(deviceName: string): Promise<UsbDriverStatus> {
  return invoke<UsbDriverStatus>('check_usb_driver_status', { deviceName });
}

// ---------------------------------------------------------------------------
// Auto-update preference (persisted on the Connector side)
// ---------------------------------------------------------------------------

export async function getAutoUpdateEnabled(): Promise<boolean> {
  return invoke<boolean>('get_auto_update_enabled');
}

export async function setAutoUpdateEnabled(enabled: boolean): Promise<void> {
  return invoke<void>('set_auto_update_enabled', { enabled });
}

export async function getLocalCoreEnabled(): Promise<boolean> {
  return invoke<boolean>('get_local_core_enabled');
}

export async function setLocalCoreEnabled(enabled: boolean): Promise<void> {
  return invoke<void>('set_local_core_enabled', { enabled });
}

export interface LocalCoreAdapterTrack {
  id: number;
  file_id: number;
  title: string;
  album_title?: string | null;
  artist_name?: string | null;
  file_path?: string | null;
  file_size?: number | null;
  duration_ms?: number | null;
  sample_rate?: number | null;
  bit_depth?: number | null;
  channels?: number | null;
  format?: string | null;
}

export interface LocalCoreAdapterAlbum {
  id: number;
  title: string;
  album_artist?: string | null;
  year?: number | null;
  track_count?: number | null;
}

export interface LocalCoreAdapterArtist {
  id: number;
  name: string;
  album_count?: number | null;
  track_count?: number | null;
}

export async function localLibraryGetAlbums(limit = 200, offset = 0): Promise<{
  enabled: boolean;
  albums: LocalCoreAdapterAlbum[];
}> {
  return invoke('local_library_get_albums', { limit, offset });
}

export async function localLibraryGetArtists(limit = 200, offset = 0): Promise<{
  enabled: boolean;
  artists: LocalCoreAdapterArtist[];
}> {
  return invoke('local_library_get_artists', { limit, offset });
}

export async function localLibraryGetTracks(limit = 500, offset = 0): Promise<{
  enabled: boolean;
  tracks: LocalCoreAdapterTrack[];
}> {
  return invoke('local_library_get_tracks', { limit, offset });
}

export async function localLibrarySearch(query: string, limit = 200): Promise<{
  enabled: boolean;
  tracks: LocalCoreAdapterTrack[];
  albums: LocalCoreAdapterAlbum[];
  artists: LocalCoreAdapterArtist[];
}> {
  return invoke('local_library_search', { query, limit });
}

// ---------------------------------------------------------------------------
// DSD / DoP capability commands
// ---------------------------------------------------------------------------

export interface DacCapabilityInfo {
  known: boolean;
  vendor: string | null;
  dop_support: boolean;
  max_dsd_rate: string | null;
  native_usb_dsd: boolean;
}

export async function getDacCapability(deviceName: string): Promise<DacCapabilityInfo> {
  return invoke<DacCapabilityInfo>('get_dac_capability', { deviceName });
}

export async function setForcePcmForDsd(enabled: boolean): Promise<void> {
  return invoke<void>('set_force_pcm_for_dsd', { enabled });
}

export async function getForcePcmForDsd(): Promise<boolean> {
  return invoke<boolean>('get_force_pcm_for_dsd');
}

// ---------------------------------------------------------------------------
// Local-library directory listing (SQLite-aware)
// ---------------------------------------------------------------------------

export interface LocalLibraryDirectoryEntry {
  name: string;
  path: string;
  isDirectory: boolean;
  sizeBytes?: number;
  modifiedUnix?: number;
  albumCount?: number;
  trackCount?: number;
}

export interface LocalLibraryDirectoryListing {
  path: string;
  parent: string | null;
  entries: LocalLibraryDirectoryEntry[];
}

function parentPathForListing(path: string): string | null {
  if (!path) return null;
  const norm = path.replace(/\\/g, '/').replace(/\/+$/, '');
  const idx = norm.lastIndexOf('/');
  if (idx <= 0) return null;
  return norm.slice(0, idx) || '/';
}

export async function listLocalLibraryDirectory(
  path: string,
): Promise<LocalLibraryDirectoryListing> {
  try {
    return await invoke<LocalLibraryDirectoryListing>('local_library_list_directory', { path });
  } catch (e: unknown) {
    const msg = String((e as { message?: string })?.message ?? e ?? '');
    const useFilesystemFallback =
      /not allowed by ACL|unknown command|command .+ not found|database is locked/i.test(msg);
    if (!useFilesystemFallback) {
      throw e;
    }
    const entries = await listDirectory(path);
    return {
      path,
      parent: parentPathForListing(path),
      entries: (entries || []).map((ent) => ({
        name: ent.name,
        path: ent.path,
        isDirectory: ent.is_dir,
      })),
    };
  }
}
